// src/utils/customerAddress.js
//
// ONE reader for a customer's service address (docs/features/ready_for_install_board_FEATURE.md §6.9).
//
// V2 stores the address once, as two views of the same place:
//   contact.address     the formatted line ("123 Main St, Springfield, IL 62701, USA") — a STRING
//   contact.addressObj  its parts: { street, city, state, zip }
// Eleven readers treated contact.address as an object (`contact.address.city` is always undefined on a string) and
// three more read the retired root `address` / `addressObj`, so the sales Lead Pool, the lead list, the archive, lead
// search, the timezone step and the calendar's new bookings all read blank for every customer (census 2026-09-14:
// contact.address is a string on all 358 customers that have one; no customer has only the root fields).
// No V1 fallbacks here (CODING_RULES §2.8). Backend counterpart: functions-shared/utils/serviceState.js.

const text = value => (typeof value === 'string' ? value.trim() : '')

function parts(customer) {
  const obj = customer?.contact?.addressObj
  return obj && typeof obj === 'object' ? obj : {}
}

/** The street part. A Studio write-back once stored the whole formatted line there; its first segment is the street. */
export function customerStreet(customer) {
  const street = text(parts(customer).street)
  return street.includes(',') ? street.split(',')[0].trim() : street
}

export function customerCity(customer) {
  return text(parts(customer).city)
}

/** Two-letter state abbreviation, upper case ('' when unknown). */
export function customerState(customer) {
  return text(parts(customer).state).toUpperCase()
}

export function customerZip(customer) {
  return text(parts(customer).zip)
}

/** The full service address line: the stored formatted line, else composed from the parts. */
export function customerAddressLine(customer) {
  const line = text(customer?.contact?.address)
  if (line) return line
  const street = customerStreet(customer)
  const city = customerCity(customer)
  const stateZip = [customerState(customer), customerZip(customer)].filter(Boolean).join(' ')
  return [street, city, stateZip].filter(Boolean).join(', ')
}

/** "City, ST" for compact cells ('' when neither is known). */
export function customerCityState(customer) {
  return [customerCity(customer), customerState(customer)].filter(Boolean).join(', ')
}

/** A Google Maps link for the service address ('' when there is no address at all). */
export function customerMapsUrl(customer) {
  const line = customerAddressLine(customer)
  if (line) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(line)}`
  const lat = customer?.contact?.latitude
  const lng = customer?.contact?.longitude
  if (
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lng)) &&
    lat !== null &&
    lng !== null
  ) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
  }
  return ''
}
